#!/usr/bin/env node

import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import {
  Tool,
  CallToolRequestSchema,
  ListToolsRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';
import dotenv from 'dotenv';

dotenv.config();

const OPENMEMORY_API_KEY = process?.env?.OPENMEMORY_API_KEY || '';
const HOST = process?.env?.HOST || 'api.openmemory.dev';
const CLIENT_NAME = process?.env?.CLIENT_NAME || 'openmemory';
const HTTP_PROTOCOL = process?.env?.HTTP_PROTOCOL || 'https';
const ASYNC_MODE = process?.env?.ASYNC_MODE || true;

// OpenMemory API base URL
const API_BASE_URL = `${HTTP_PROTOCOL}://${HOST}`;

// Tool definitions
const ADD_MEMORY_TOOL: Tool = {
  name: 'add-memory',
  description:
    'Add a new memory. This method is called everytime the user informs anything about themselves, their preferences, or anything that has any relevent information whcih can be useful in the future conversation. This can also be called when the user asks you to remember something.',
  inputSchema: {
    type: 'object',
    properties: {
      content: {
        type: 'string',
        description: 'The content to store in memory',
      },
    },
    required: ['content'],
  },
};

const SEARCH_MEMORIES_TOOL: Tool = {
  name: 'search-memories',
  description: 'Search through stored memories. This method is called ANYTIME the user asks anything.',
  inputSchema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: "The search query. This is the query that the user has asked for. Example: 'What did I tell you about the weather last week?' or 'What did I tell you about my friend John?'",
      },
    },
    required: ['query'],
  },
};

const LIST_MEMORIES_TOOL: Tool = {
  name: 'list-memories',
  description: 'List all memories in the user\'s memory storage.',
  inputSchema: {
    type: 'object',
    properties: {},
    required: [],
  },
};

const DELETE_ALL_MEMORIES_TOOL: Tool = {
  name: 'delete-all-memories',
  description: 'Delete all memories in the user\'s memory storage. Use with caution as this action cannot be undone.',
  inputSchema: {
    type: 'object',
    properties: {},
    required: [],
  },
};

// Create server instance
const server = new Server(
  {
    name: 'openmemory',
    version: '0.0.1',
  },
  {
    capabilities: {
      tools: {},
      logging: {},
    },
  }
);

// Helper function to add memories
async function addMemory(content: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/mcp/add_memories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENMEMORY_API_KEY}`,
      },
      body: JSON.stringify({
        text: content,
        client_name: CLIENT_NAME,
        async_mode: ASYNC_MODE || true,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.status === 'success';
  } catch (error) {
    console.error('Error adding memory:', error);
    return false;
  }
}

// Helper function to search memories
async function searchMemories(query: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/mcp/search_memory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENMEMORY_API_KEY}`,
      },
      body: JSON.stringify({
        query: query,
        client_name: CLIENT_NAME,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.memories || [];
  } catch (error) {
    console.error('Error searching memories:', error);
    return [];
  }
}

// Helper function to list all memories
async function listMemories() {
  try {
    const response = await fetch(`${API_BASE_URL}/mcp/list_memories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENMEMORY_API_KEY}`,
      },
      body: JSON.stringify({
        client_name: CLIENT_NAME,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.memories || [];
  } catch (error) {
    console.error('Error listing memories:', error);
    return [];
  }
}

// Helper function to delete all memories
async function deleteAllMemories() {
  try {
    const response = await fetch(`${API_BASE_URL}/mcp/delete_all_memories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENMEMORY_API_KEY}`,
      },
      body: JSON.stringify({
        client_name: CLIENT_NAME,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting all memories:', error);
    return { status: 'error', message: 'Failed to delete memories' };
  }
}

// Register tool handlers
server.setRequestHandler(ListToolsRequestSchema, async () => ({
  tools: [ADD_MEMORY_TOOL, SEARCH_MEMORIES_TOOL, LIST_MEMORIES_TOOL, DELETE_ALL_MEMORIES_TOOL],
}));

server.setRequestHandler(CallToolRequestSchema, async (request) => {
  try {
    const { name, arguments: args } = request.params;
    
    if (!args) {
      throw new Error('No arguments provided');
    }
    
    switch (name) {
      case 'add-memory': {
        const { content } = args as { content: string };
        await addMemory(content);
        return {
          content: [
            {
              type: 'text',
              text: 'Memory added successfully',
            },
          ],
          isError: false,
        };
      }
      
      case 'search-memories': {
        const { query } = args as { query: string };
        const results = await searchMemories(query);
        const formattedResults = results.map((result: any) => 
          `Memory: ${result.memory || result.text}\nRelevance: ${result.score || 'N/A'}\n---`
        ).join('\n');
        
        return {
          content: [
            {
              type: 'text',
              text: formattedResults || 'No memories found',
            },
          ],
          isError: false,
        };
      }
      
      case 'list-memories': {
        const results = await listMemories();
        const formattedResults = results.map((result: any, index: number) => 
          `${index + 1}. Memory: ${result.memory || result.text}\nID: ${result.id || 'N/A'}\n---`
        ).join('\n');
        
        return {
          content: [
            {
              type: 'text',
              text: formattedResults || 'No memories found',
            },
          ],
          isError: false,
        };
      }
      
      case 'delete-all-memories': {
        const result = await deleteAllMemories();
        const message = result.status === 'success' 
          ? result.message || 'All memories deleted successfully'
          : result.message || 'Failed to delete memories';
        
        return {
          content: [
            {
              type: 'text',
              text: message,
            },
          ],
          isError: result.status !== 'success',
        };
      }
      
      default:
        return {
          content: [
            { type: 'text', text: `Unknown tool: ${name}` },
          ],
          isError: true,
        };
    }
  } catch (error) {
    return {
      content: [
        {
          type: 'text',
          text: `Error: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
      isError: true,
    };
  }
});

// Function to log safely
function safeLog(
  level: 'error' | 'debug' | 'info' | 'notice' | 'warning' | 'critical' | 'alert' | 'emergency',
  data: any
): void {
  // For stdio transport, log to stderr to avoid protocol interference
  console.error(`[${level}] ${typeof data === 'object' ? JSON.stringify(data) : data}`);
  
  // Send to logging capability if available
  try {
    server.sendLoggingMessage({ level, data });
  } catch (error) {
    // Ignore errors when logging is not available
  }
}

// Server startup
async function main() {
  try {
    console.error('Initializing OpenMemory MCP Server...');
    console.error(`Connecting to OpenMemory API at: ${API_BASE_URL}`);
    console.error(`Using client name: ${CLIENT_NAME}`);
    
    const transport = new StdioServerTransport();
    await server.connect(transport);
    
    safeLog('info', 'OpenMemory MCP Server initialized successfully');
    console.error('OpenMemory MCP Server running on stdio');
  } catch (error) {
    console.error('Fatal error running server:', error);
    process.exit(1);
  }
}

main().catch((error) => {
  console.error('Fatal error in main():', error);
  process.exit(1);
});